/* eslint-disable no-nested-ternary */
import React, { ChangeEvent, FocusEvent, useState } from 'react';
import { CommonLabeledTextInputProps } from '../../../types/common';
import ErrorLabel from '../ErrorLabel';
import { InnerLabelTestId } from '../InnerLabel';
import { TextboxTestId } from '../Textbox';

export const LabeledTextboxTestId = 'labeledtextbox-input';
interface PhoneNumberInputFieldProps {
  handlePhoneInputChange?: (
    input: React.ChangeEvent<HTMLInputElement>
  ) => string; // Existing props
}

export type LabeledTextboxProps = CommonLabeledTextInputProps &
  PhoneNumberInputFieldProps;

const LabeledTextbox: React.FC<LabeledTextboxProps> = ({
  autoFocus = false,
  name,
  label,
  type,
  inputMode,
  pattern,
  min,
  max,
  step,
  errorLabel,
  className = '',
  inputClassName = '',
  labelClassName,
  icon,
  autoComplete,
  defaultValue,
  maxLength,
  disabled,
  register,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  onKeyUp,
  onKeyPress,
  readOnly,
  value,
  disablePlaceholder,
  handlePhoneInputChange,
}: LabeledTextboxProps) => {
  const {
    onChange: rhfOnChange,
    onBlur: rhfOnBlur,
    ...rest
  } = register?.(name) || {};
  const [phoneNumber, setPhoneNumber] = useState(defaultValue || '');
  const readOnlyInputStyle = readOnly ? 'text-gray-400' : '';
  const inputClasses = `peer ${inputClassName || ''}
      pt-5 w-full pl-4 pb-1 ${readOnlyInputStyle} checkout-panel-form-input
      border-1-half rounded border-transparent outline-none placeholder-transparent
      focus:border-[color:var(--form-accent-color)] ${
        errorLabel ? 'input-error-border' : ''
      }`;

  const dynamicLabelClasses = `peer-focus:text-sm peer-focus:left-4 peer-focus:top-1
                              peer-placeholder-shown:text-base
                              peer-placeholder-shown:left-4 peer-placeholder-shown:top-4`;
  const staticLabelClasses = 'text-sm left-4';

  const labelClasses = `${labelClassName || ''} absolute
      text-xs left-4 top-1 transition-all checkout-panel-form-label
      ${disablePlaceholder ? staticLabelClasses : dynamicLabelClasses}
      ${errorLabel ? 'input-error-text' : ''}`;

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>): void => {
    rhfOnChange?.(event);
    onChange?.(event);
  };

  const handleOnBlur = (event: FocusEvent<HTMLInputElement>): void => {
    rhfOnBlur?.(event);
    onBlur?.(event);
  };

  const usePhoneFormatter = !!(type === 'tel' && handlePhoneInputChange);

  const handlePhone = (event: ChangeEvent<HTMLInputElement>): void => {
    rhfOnChange?.(event);
    onChange?.(event);

    if (usePhoneFormatter) {
      const phone = handlePhoneInputChange(event);
      setPhoneNumber(phone);
    }
  };

  return (
    <div
      className={`m-2 ${className} relative`}
      data-testid={LabeledTextboxTestId}
    >
      {!register && (
        <input
          id={name}
          name={name}
          placeholder={name}
          type={type}
          inputMode={inputMode}
          pattern={pattern}
          min={min}
          max={max}
          maxLength={maxLength}
          disabled={disabled}
          readOnly={readOnly}
          step={step}
          className={inputClasses}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onKeyPress={onKeyPress}
          onFocus={onFocus}
          data-testid={TextboxTestId}
          autoCorrect="off"
          autoComplete={autoComplete}
          defaultValue={defaultValue}
          value={value}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          onChange={onChange}
          onBlur={onBlur}
        />
      )}
      {register && usePhoneFormatter ? (
        <input
          id={name}
          placeholder={name}
          type={type}
          inputMode={inputMode}
          pattern={pattern}
          min={min}
          max={max}
          maxLength={maxLength}
          disabled={disabled}
          readOnly={readOnly}
          step={step}
          className={`pt-5 w-full pl-4 pb-1 ${inputClasses}`}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          data-testid={TextboxTestId}
          autoCorrect="off"
          autoComplete={autoComplete}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          value={phoneNumber}
          onChange={handlePhone}
          onBlur={handleOnBlur}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...rest}
        />
      ) : register ? (
        <input
          id={name}
          placeholder={name}
          type={type}
          inputMode={inputMode}
          pattern={pattern}
          min={min}
          max={max}
          maxLength={maxLength}
          disabled={disabled}
          readOnly={readOnly}
          step={step}
          className={`pt-5 w-full pl-4 pb-1 ${inputClasses}`}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          data-testid={TextboxTestId}
          autoCorrect="off"
          autoComplete={autoComplete}
          defaultValue={defaultValue}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...rest}
        />
      ) : null}
      <label
        htmlFor={name}
        className={labelClasses}
        data-testid={InnerLabelTestId}
      >
        {label}
      </label>
      {icon}
      <ErrorLabel>{errorLabel}</ErrorLabel>
    </div>
  );
};
export default LabeledTextbox;
